import { Component, Input, OnInit } from '@angular/core';
import { forkJoin } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';

import { NotificationPreferenceService } from 'src/app/shared/services/notification-preference.service';
import {
  AccountNotificationPreferencePayload,
  ClientNotificationPreferencePayload,
  NotificationEventType,
  NotificationPreferencesListResponse,
} from 'src/app/shared/dto/notfication-preference';
import { NotificationService } from 'src/app/utils/notification.service';
import { AccountType } from 'src/app/shared/models/account';
import { ClientSetupService } from 'src/app/pages/settings/services/client-setup.service';
import { Permissions } from 'src/app/shared/dto/permissions';
import { PermissionService } from 'src/app/utils/permission.service';

export interface NotificationRow {
  label: string;
  event: NotificationEventType;
  emailEnabled: boolean;
  smsEnabled: boolean;
  hasInput?: boolean;
  inputValue?: number;
}

@Component({
  selector: 'app-notification-preferences-table',
  templateUrl: './notification-preferences-table.component.html',
  styleUrls: ['./notification-preferences-table.component.css'],
})
export class NotificationPreferencesTableComponent implements OnInit {
  @Input() accountId: number;
  @Input() accountType: AccountType;
  @Input() clientType?: string;
  @Input() notificationEvents: {
    label: string;
    event: NotificationEventType;
    hasInput?: boolean;
  }[];
  @Input() deferredSave = false; // When true, changes won't be saved immediately
  @Input() editPermission: Permissions;

  public Permissions = Permissions;

  public loading = true;
  public saving = false;
  public hasUnsavedChanges = false;

  public displayedColumns: string[] = ['event', 'email', 'sms'];
  public dataSource = new MatTableDataSource<NotificationRow>([]);

  public isSmsEnabled = true;
  public isEmailEnabled = true;

  notificationRows: NotificationRow[] = [];
  private originalInputValues = new Map<NotificationEventType, number>();
  private originalPreferences = new Map<
    NotificationEventType,
    { emailEnabled: boolean; smsEnabled: boolean; inputValue?: number }
  >();
  private payload:
    | AccountNotificationPreferencePayload
    | ClientNotificationPreferencePayload;

  public emailDisabledToolTip: string = $localize`:@@email_disabled_tooltip:Email notifications are disabled by setup`;
  public smsDisabledToolTip: string = $localize`:@@sms_disabled_tooltip:SMS notifications are disabled by setup`;
  public noEditPermissionTooltip: string = $localize`:@@no_edit_notification_permission:You do not have permission to edit notification preferences`;
  
  public get canEdit(): boolean {
    return this.permissionService.hasPermissionList([this.editPermission]);
  }

  constructor(
    private notificationPreferenceService: NotificationPreferenceService,
    private clientSetupService: ClientSetupService,
    private toastr: NotificationService,
    private permissionService: PermissionService,
  ) {}

  ngOnInit(): void {
    forkJoin({
      masterSwitch: this.clientSetupService.getClientData(),
      preferences: this.getNotificationPreferences(),
    }).subscribe({
      next: ({ masterSwitch, preferences }) => {
        this.isSmsEnabled = !!masterSwitch.data.integrations.enableSmsNotifications;
        this.isEmailEnabled = !!masterSwitch.data.integrations.enableEmailNotifications;

        this.initializeNotificationRows();
        this.applyPreferences(preferences);
      },
      error: () => {
        this.toastr.error(
          $localize`:@@failed_to_load_client_settings:Failed to load client settings`
        );
      },
    });
  }

  // ------------------------------------------------------------------
  // DATA LOADING
  // ------------------------------------------------------------------
  private getNotificationPreferences() {
    this.loading = true;
    return this.notificationPreferenceService
      .getNotificationPreferences(this.accountType, this.accountId, this.clientType)
      .pipe(
        take(1),
        finalize(() => (this.loading = false))
      );
  }

  private initializeNotificationRows(): void {
    if (!this.notificationEvents?.length) return;

    this.notificationRows = this.notificationEvents.map((eventConfig) => ({
      label: eventConfig.label,
      event: eventConfig.event,
      emailEnabled: false,
      smsEnabled: false,
      hasInput: !!eventConfig.hasInput,
      inputValue: eventConfig.hasInput ? undefined : 1,
    }));

    this.dataSource.data = this.notificationRows;
  }

  private applyPreferences(
    response: NotificationPreferencesListResponse
  ): void {
    response.data.forEach((preference) => {
      this.originalInputValues.set(preference.event, preference.reminderValue);

      const row = this.notificationRows.find(
        (notificationRow) => notificationRow.event === preference.event
      );
      if (!row) return;

      row.emailEnabled = preference.emailEnabled;
      row.smsEnabled = preference.smsEnabled;

      if (row.hasInput && preference.reminderValue !== undefined) {
        row.inputValue = preference.reminderValue;
      }
    });

    // Store original state for ALL rows (for change detection)
    this.notificationRows.forEach((row) => {
      this.originalPreferences.set(row.event, {
        emailEnabled: row.emailEnabled,
        smsEnabled: row.smsEnabled,
        inputValue: row.inputValue,
      });
    });

    this.dataSource.data = this.notificationRows;
  }

  private refreshPreferences(): void {
    this.getNotificationPreferences().subscribe({
      next: (preferences) => this.applyPreferences(preferences),
    });
  }

  // ------------------------------------------------------------------
  // UI HELPERS
  // ------------------------------------------------------------------
  public isAllSelected(channel: 'email' | 'sms'): boolean {
    return channel === 'email'
      ? this.notificationRows.every((r) => r.emailEnabled)
      : this.notificationRows.every((r) => r.smsEnabled);
  }

  public isSomeSelected(channel: 'email' | 'sms'): boolean {
    const selected =
      channel === 'email'
        ? this.notificationRows.filter((r) => r.emailEnabled).length
        : this.notificationRows.filter((r) => r.smsEnabled).length;

    return selected > 0 && selected < this.notificationRows.length;
  }

  // ------------------------------------------------------------------
  // SINGLE TOGGLE
  // ------------------------------------------------------------------
  public onTogglePreference(
    row: NotificationRow,
    channel: 'email' | 'sms'
  ): void {
    if (
      this.saving ||
      (channel === 'email' && !this.isEmailEnabled) ||
      (channel === 'sms' && !this.isSmsEnabled)
    ) {
      return;
    }

    channel === 'email'
      ? (row.emailEnabled = !row.emailEnabled)
      : (row.smsEnabled = !row.smsEnabled);

    // If deferredSave is enabled, just update state and check for changes
    if (this.deferredSave) {
      this.checkForChanges();
      return;
    }

    // Original immediate save behavior
    this.saving = true;
    this.payload = this.buildPayload([row]);

    this.notificationPreferenceService
      .updateNotificationPreference(this.accountType, this.payload)
      .pipe(
        take(1),
        finalize(() => (this.saving = false))
      )
      .subscribe({
        next: () => {
          this.toastr.success(
            $localize`:@@notification_preference_updated:Notification preference updated successfully`
          );
          this.refreshPreferences();
        },
        error: () => {
          channel === 'email'
            ? (row.emailEnabled = !row.emailEnabled)
            : (row.smsEnabled = !row.smsEnabled);

          this.toastr.error(
            $localize`:@@notification_preference_update_failed:Failed to update notification preference`
          );
        },
      });
  }

  // ------------------------------------------------------------------
  // BULK TOGGLE
  // ------------------------------------------------------------------
  public toggleAllPreferences(channel: 'email' | 'sms'): void {
    if (
      this.saving ||
      (channel === 'email' && !this.isEmailEnabled) ||
      (channel === 'sms' && !this.isSmsEnabled)
    ) {
      return;
    }

    const newValue = !this.isAllSelected(channel);

    this.notificationRows.forEach((row) => {
      channel === 'email'
        ? (row.emailEnabled = newValue)
        : (row.smsEnabled = newValue);
    });

    this.dataSource.data = this.notificationRows;

    // If deferredSave is enabled, just update state and check for changes
    if (this.deferredSave) {
      this.checkForChanges();
      return;
    }

    // Original immediate save behavior
    this.saving = true;
    this.payload = this.buildPayload(this.notificationRows);

    this.notificationPreferenceService
      .updateNotificationPreference(this.accountType, this.payload)
      .pipe(
        take(1),
        finalize(() => (this.saving = false))
      )
      .subscribe({
        next: () => {
          this.toastr.success(
            $localize`:@@notification_preferences_updated:Notification preferences updated successfully`
          );
          this.refreshPreferences();
        },
        error: () => {
          this.notificationRows.forEach((row) => {
            channel === 'email'
              ? (row.emailEnabled = !newValue)
              : (row.smsEnabled = !newValue);
          });

          this.dataSource.data = this.notificationRows;
          this.toastr.error(
            $localize`:@@notification_preferences_update_failed:Failed to update notification preferences`
          );
        },
      });
  }

  // ------------------------------------------------------------------
  // INPUT CHANGE
  // ------------------------------------------------------------------
  public onInputValueChange(row: NotificationRow): void {
    if (this.saving) return;

    // If deferredSave is enabled, just update state and check for changes
    if (this.deferredSave) {
      this.checkForChanges();
      return;
    }

    // Original immediate save behavior - check if value actually changed
    const original = this.originalInputValues.get(row.event);
    if (
      row.inputValue === original ||
      !row.inputValue ||
      row.inputValue < 1 ||
      (!row.emailEnabled && !row.smsEnabled)
    ) {
      return;
    }

    this.saving = true;
    this.payload = this.buildPayload([row]);

    this.notificationPreferenceService
      .updateNotificationPreference(this.accountType, this.payload)
      .pipe(
        take(1),
        finalize(() => (this.saving = false))
      )
      .subscribe({
        next: () => {
          this.toastr.success(
            $localize`:@@notification_preference_updated:Notification preference updated successfully`
          );
          this.refreshPreferences();
        },
        error: () => {
          this.toastr.error(
            $localize`:@@notification_preference_update_failed:Failed to update notification preference`
          );
        },
      });
  }

  // ------------------------------------------------------------------
  // PAYLOAD BUILDER (DRY)
  // ------------------------------------------------------------------
  private buildPayload(rows: NotificationRow[]) {
    const preferences = rows.map((row) => ({
      event: row.event,
      emailEnabled: row.emailEnabled,
      smsEnabled: row.smsEnabled,
      ...(row.hasInput && { reminderValue: row.inputValue || 1 }),
    }));

    return (this.accountType === 'CLIENT' || this.accountType === 'CORPORATE')
      ? {
          accountType: this.accountType,
          clientType: this.clientType || 'INDIVIDUAL',
          clientId: this.accountId,
          preferences,
        }
      : {
          accountType: this.accountType,
          accountId: this.accountId,
          preferences,
        };
  }

  // ------------------------------------------------------------------
  // CHANGE DETECTION & DEFERRED SAVE
  // ------------------------------------------------------------------
  private checkForChanges(): void {
    this.hasUnsavedChanges = this.notificationRows.some((row) => {
      const original = this.originalPreferences.get(row.event);
      if (!original) return false;

      return (
        row.emailEnabled !== original.emailEnabled ||
        row.smsEnabled !== original.smsEnabled ||
        row.inputValue !== original.inputValue
      );
    });
  }

  public onSaveClick(): void {
    if (this.saving || !this.deferredSave || !this.hasUnsavedChanges) return;

    this.saving = true;
    this.payload = this.buildPayload(this.notificationRows);

    this.notificationPreferenceService
      .updateNotificationPreference(this.accountType, this.payload)
      .pipe(
        take(1),
        finalize(() => {
          this.saving = false;
        })
      )
      .subscribe({
        next: () => {
          this.toastr.success(
            $localize`:@@notification_preferences_updated:Notification preferences updated successfully`
          );
          this.refreshPreferences();
          this.hasUnsavedChanges = false;
        },
        error: () => {
          this.toastr.error(
            $localize`:@@notification_preferences_update_failed:Failed to update notification preferences`
          );
        },
      });
  }
}
